import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to add real web push notifications to your webapp",
  "description": "",
  "published": "2017-11-16T00:00:00.000Z",
  "redirect_from": ["/blog/add-real-web-push-notifications-webapp/swizec/7924"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`You've probably seen web notifications before. YouTube shows them when it goes to a new song, Facebook pings them when a new message comes in, scammy websites ask for permissions and you say no. The usual.`}</p>
    <p>{`You can fire those notifications from anywhere inside your JavaScript.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const notification = new Notification(title, {
  body: body,
  icon: iconURL,
});
`}</code></pre>
    <p>{`That creates a new browser notification and shows it to the user. Since Chrome 60-something on a Mac, they're integrated with native notifications. It's great.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/Screen-Shot-2017-11-16-at-09.37.29.png",
        "alt": null
      }}></img></p>
    <p>{`You can close the browser notification with `}<inlineCode parentName="p">{`notification.close`}</inlineCode>{`, and you can listen for click events with `}<inlineCode parentName="p">{`notification.onclick = () => ...`}</inlineCode>{`. Most often you'd want to use that click event to focus on your browser tab.`}</p>
    <p>{`To make this work however, the user must keep your webapp open and you must keep your fingers crossed the browser hasn't throttled your JavaScript too much for inactivity. Browsers do that, you know.`}</p>
    <p>{`But did you know you can also add `}<em parentName="p">{`real`}</em>{` push notifications to your webapp? The kind that come from a server and work even with no open tabs? Oh yes, just like a mobile app.`}</p>
    <p>{`I had no idea this has been part of Chrome since version 40-something. Firefox supports it, too. Safari and Internet Explorer, no.`}</p>
    <p>{`It is not a part of the JavaScript standard yet. `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/Push_API"
      }}>{`MDN lists web push notification support as experimental`}</a>{`.`}</p>
    <p>{`Here's what you'll need to make it work 👇`}</p>
    <ul>
      <li parentName="ul">{`a service worker`}</li>
      <li parentName="ul">{`some code to register the service worker`}</li>
      <li parentName="ul">{`some code to ask for notification permissions`}</li>
      <li parentName="ul">{`bit of code to subscribe to web push notifications`}</li>
      <li parentName="ul">{`a server to trigger the push notification`}</li>
    </ul>
    <h2 {...{
      "id": "service-worker-that-listens-for-push-events"
    }}>{`Service worker that listens for push events`}</h2>
    <p>{`You can think of service workers as a piece of JavaScript that lives in your user's browser and does stuff. They can do stuff even when your site isn't loaded, or even open.`}</p>
    <p>{`Most commonly they're used for caching in what's called progressive web apps – PWA. They intercept requests and serve code from a local cache. Even if the browser is offline.`}</p>
    <p>{`But none of that right now. We're using them for push notifications.`}</p>
    <p>{`A service worker will live in our user's browser and listen for `}<inlineCode parentName="p">{`push`}</inlineCode>{` events. The browser gets these events from a so-called push service, triggers the service worker, and our service worker does whatever.`}</p>
    <p>{`For security reasons, we can't control `}<em parentName="p">{`which`}</em>{` push service the browser uses. The browser tells us instead. You'll see how in the section about subscribing.`}</p>
    <p>{`For privacy reasons, we also have to accept a sort of gentleman's agreement with the browser where we promise to always show a notification when a push event comes in.`}</p>
    <p>{`So… a service worker is just a JavaScript file. Nothing fancy going on. You use `}<inlineCode parentName="p">{`self`}</inlineCode>{` to refer to the global scope because there's no `}<inlineCode parentName="p">{`window`}</inlineCode>{` and no `}<inlineCode parentName="p">{`document`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// service-worker.js

function showNotification(event) {
    return new Promise(resolve => {
        const { body, title, tag } = JSON.parse(event.data.text());

        self.registration
            .getNotifications({ tag })
            .then(existingNotifications => { // close? ignore? })
            .then(() => {
                const icon = \`/path/to/icon\`;
                return self.registration
                    .showNotification(title, { body, tag, icon })
            })
            .then(resolve)
    })
}

self.addEventListener("push", event => {
 event.waitUntil(
    showNotification(event)
        );
    }
});

self.addEventListener("notificationclick", event => {
    event.waitUntil(clients.openWindow("/"));
});
`}</code></pre>
    <p>{`That's all the code that goes into our service worker.`}</p>
    <p>{`In `}<inlineCode parentName="p">{`showNotification`}</inlineCode>{`, we return a `}<inlineCode parentName="p">{`Promise`}</inlineCode>{` that resolves once we're done showing our notification.`}</p>
    <p>{`We use `}<inlineCode parentName="p">{`JSON.parse`}</inlineCode>{` to unpack notification parameters using the convention of putting JSON into the `}<inlineCode parentName="p">{`message`}</inlineCode>{` portion of our push notification.`}</p>
    <p>{`Before showing our push notification with `}<inlineCode parentName="p">{`self.registration.showNotification`}</inlineCode>{`, we can clear any existing notifications with the same `}<inlineCode parentName="p">{`tag`}</inlineCode>{` using `}<inlineCode parentName="p">{`self.registration.getNotifications`}</inlineCode>{`. This gives us a list of notifications that we can `}<inlineCode parentName="p">{`.close`}</inlineCode>{`.`}</p>
    <p>{`I've noticed (at least on a Mac) that subsequent notifications with the same `}<inlineCode parentName="p">{`tag`}</inlineCode>{` sometimes don't show up unless you close the previous ones.`}</p>
    <p>{`We use `}<inlineCode parentName="p">{`self.addEventListener`}</inlineCode>{` to listen for both `}<inlineCode parentName="p">{`push`}</inlineCode>{` and `}<inlineCode parentName="p">{`onclick`}</inlineCode>{` events. In the push handler, we call `}<inlineCode parentName="p">{`showNotification`}</inlineCode>{` to show our web push notification, and in `}<inlineCode parentName="p">{`notificationclick`}</inlineCode>{` we open our site.`}</p>
    <p>{`That's the service worker 😄`}</p>
    <h2 {...{
      "id": "register-the-service-worker"
    }}>{`Register the service worker`}</h2>
    <p>{`Registering said service worker happens in our regular JavaScript code. Usually in the main `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` entry point because there can be only one service worker for an entire domain.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function registerServiceWorker() {
  navigator.serviceWorker
    .register("/service-worker.js")
    .then((registration) => {
      console.log("ServiceWorker registered with scope:", registration.scope);
    })
    .catch((e) => console.error("ServiceWorker failed:", e));
}

if (navigator && navigator.serviceWorker) {
  registerServiceWorker();
}
`}</code></pre>
    <p>{`We call `}<inlineCode parentName="p">{`navigator.serviceWorker.registe`}</inlineCode>{` with the URL of our service worker and wait for the promise to resolve. If all goes well, we print a success message, otherwise we print an error.`}</p>
    <p>{`This is not a piece of code you'll have to write often. Once per project at most. Many just find it online and copypasta when they need it, I think.`}</p>
    <p>{`One caveat to keep in mind is that service workers are limited to the scope they're served from. You can't serve that file from a CDN, or from a `}<inlineCode parentName="p">{`static.domain.com`}</inlineCode>{`, or even `}<inlineCode parentName="p">{`domain.com/static/`}</inlineCode>{`.`}</p>
    <p>{`This is a security precaution.`}</p>
    <p>{`My solution was to use the Webpack `}<a parentName="p" {...{
        "href": "https://github.com/idiotWu/sw-loader"
      }}><inlineCode parentName="a">{`sw-loader`}</inlineCode></a>{` and configure it to compile this particular file into a different location and with a different publicPath than all other JavaScript, which goes into CDNs and has fingerprinting and stuff.`}</p>
    <p>{`That part was tricky, but it's very specific to every webapp, so it’s not a good candidate for this article :)`}</p>
    <h2 {...{
      "id": "ask-for-web-notification-permissions"
    }}>{`Ask for web notification permissions`}</h2>
    <p>{`If you've ever used web notifications before, you already know this part: You have to ask the user for permission.`}</p>
    <p>{`Here's how that goes`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const permission = Notification.requestPermission();

if (permission !== "granted") {
  // no notifications
} else {
  // yay notifications
}
`}</code></pre>
    <p>{`Yep, that's it.`}</p>
    <p>{`Running `}<inlineCode parentName="p">{`Notification.requestPermission`}</inlineCode>{` pops up a little dialog for our user asking them to grant us web notification permissions. If they do, the permission is set to `}<inlineCode parentName="p">{`granted`}</inlineCode>{`. Other options include `}<inlineCode parentName="p">{`denied`}</inlineCode>{`, and I think it stays null if they ignore us.`}</p>
    <p>{`You can check permission status at any time with `}<inlineCode parentName="p">{`Notification.permission`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "subscribe-to-web-push-notifications"
    }}>{`Subscribe to web push notifications`}</h2>
    <p>{`Here comes the interesting part: subscribing to web push notifications. This is where we ask the browser `}<em parentName="p">{`"Hey, which push service do you wanna use?"`}</em></p>
    <p>{`The process has 2 to 3 steps depending on how you count 👇`}</p>
    <ol>
      <li parentName="ol">{`Create VAPID keys for our server, one-time`}</li>
      <li parentName="ol">{`Ask browser to subscribe`}</li>
      <li parentName="ol">{`Save subscription info for our user`}</li>
    </ol>
    <p><a parentName="p" {...{
        "href": "https://blog.mozilla.org/services/2016/04/04/using-vapid-with-webpush/"
      }}>{`VAPID keys`}</a>{` are a way for our server to identify itself with the push service. That way our user's browser can be sure that push notifications are coming from us and not from some random spammer who got in the way.`}</p>
    <p>{`I used the `}<a parentName="p" {...{
        "href": "https://github.com/zaru/webpush"
      }}><inlineCode parentName="a">{`Webpush`}</inlineCode></a>{` gem to generate these keys, there's a `}<a parentName="p" {...{
        "href": "https://github.com/web-push-libs/web-push"
      }}><inlineCode parentName="a">{`web-push`}</inlineCode></a>{` package for node as well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`# One-time, on the server
vapid_key = Webpush.generate_key

# Save these in your application server settings
vapid_key.public_key
vapid_key.private_key
`}</code></pre>
    <p>{`You will have to send the `}<inlineCode parentName="p">{`public_key`}</inlineCode>{` to your frontend somehow because you'll need it to subscribe to web push notifications. The subscription itself happens like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function subscribeToPushNotifications(registration) {
  return registration.pushManager
    .subscribe({
      userVisibleOnly: true,
      applicationServerKey: window.vapidPublicKey,
    })
    .then((pushSubscription) => {
      console.log(
        "Received PushSubscription:",
        JSON.stringify(pushSubscription)
      );
      return pushSubscription;
    });
}
`}</code></pre>
    <p>{`We call `}<inlineCode parentName="p">{`registration.pushManager.subscribe`}</inlineCode>{` to subscribe with our `}<inlineCode parentName="p">{`applicationServerKey`}</inlineCode>{`. That's the `}<inlineCode parentName="p">{`vapid.public_key`}</inlineCode>{`. And as I mentioned earlier, we promise to always show a notification with `}<inlineCode parentName="p">{`userVisibleOnly: true`}</inlineCode>{`.`}</p>
    <p>{`Not a single browser currently supports `}<inlineCode parentName="p">{`userVisibleOnly: false`}</inlineCode>{` because it could lead to privacy issues. Think sending a push and having your service worker send back detailed GPS location for your user. No bueno.`}</p>
    <p>{`That `}<inlineCode parentName="p">{`pushSubscription`}</inlineCode>{` will have a bunch of data inside. Everything from which API endpoint to use when sending notifications to info about how to authenticate with the service.`}</p>
    <p>{`The browser is in full control.`}</p>
    <p>{`You should save that info on the backend and associate it with your user. As far as I can tell, it's unique for every subscription request and definitely for every browser.`}</p>
    <p>{`The tricky part here is when the same user is using multiple browsers and devices. If you want to send push notifications to all of them, you're going to have to keep multiple copies of this info.`}</p>
    <h2 {...{
      "id": "trigger-a-web-push-notification"
    }}>{`Trigger a web push notification`}</h2>
    <p>{`To trigger a web push notification from your server, I suggest using a library. Something like `}<a parentName="p" {...{
        "href": "https://github.com/zaru/webpush"
      }}>{`Webpush for Ruby`}</a>{` or `}<a parentName="p" {...{
        "href": "https://github.com/web-push-libs/web-push"
      }}>{`web-push for node`}</a>{`. I'm sure libraries exist for other languages as well.`}</p>
    <p>{`Here's how you'd send a notification in Ruby/Rails:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`def send_web_push_notification(user_id)
    subscription = User.find(user_id).web_push_subscription

    message = {
        title: "You have a message!",
        body: "This is the message body",
        tag: "new-message"
    }

    unless subscription.nil?
        Webpush.payload_send(
            message: JSON.generate(message),
            endpoint: subscription["endpoint"],
            p256dh: subscription["keys"]["p256dh"],
            auth: subscription["keys"]["auth"],
            ttl: 15,
            vapid: {
                subject: 'mailto:admin@example.com',
          public_key: Rails.application.config.webpush_keys[:public_key],
          private_key: Rails.application.config.webpush_keys[:private_key]
        }
        )
    end
end
`}</code></pre>
    <p>{`We use a database JSON field called `}<inlineCode parentName="p">{`web_push_subscription`}</inlineCode>{` to save the `}<inlineCode parentName="p">{`pushSubscription`}</inlineCode>{` info on our users.`}</p>
    <p>{`If that field has info, we can use `}<inlineCode parentName="p">{`Webpush`}</inlineCode>{` to send a notification to the API. The API then sends it out to our service worker, which then shows a notification.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`You should now be able to add web push notifications to your webapp. I left out some details around setting up Webpack to serve service worker code correctly, but we can get into that some other day.`}</p>
    <p>{`If you do decide to add push notifications to your webapp, please use them responsibly. Remember what happened to mobile app notifications and what a mess that has become.`}</p>
    <p>{`Wouldn't want to train users to automatically deny notification permissions now would we :)`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      